import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useState } from 'react';
import TrashIcon from '@/assets/trash.svg';
import UnlinkIcon from '@/assets/unlink.svg';
import ViewIcon from '@/assets/view.svg';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerProps } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import { Modal } from '@/components/ui/modal';
import { StatusBadge } from '@/components/ui/status-badge';
import { strings } from '@/constants/strings';
import { useCloseInvalidDrawer } from '@/hooks/use-close-invalid-drawer';
import { useUnassociatedDevices } from '@/hooks/use-devices';
import { useOpenState } from '@/hooks/use-open-state';
import { useOrganisations } from '@/hooks/use-organisations';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { ViewDevice } from '@/pages/authenticated/groups/group/unit/drawer-views/view-device';
import {
  useAddDeviceToOrganisationMutation,
  useDeleteDeviceMutation,
  useRemoveDeviceFromOrganisationMutation,
} from '@/redux/api/endpoints/device';
import {
  composeTableOptions,
  getEntityRowId,
  getFilteredSelectedRows,
} from '@/table';
import { AnyDevice, DeviceAssignedToOrganisation } from '@/types/device';
import { cx } from '@/utils/cx';
import {
  getDeviceStatusDisplay,
  getDeviceTypeLabel,
} from '@/utils/device-helpers';
import { AssignDevicesForm } from './drawer-views/assign-devices-form';
import { SelectedDevicesActionBar } from './selected-devices-action-bar';

const hasOrganisation = (d: AnyDevice): d is DeviceAssignedToOrganisation =>
  d.organisation !== undefined;

const columns: MRT_ColumnDef<AnyDevice>[] = [
  {
    id: 'serialNumber',
    accessorFn: ({ meta }) => meta?.serialNumber,
    header: 'Device Serial Number (DSN)',
    size: 150,
  },
  {
    id: 'type',
    accessorFn: ({ type }) => getDeviceTypeLabel(type),
    header: 'Type',
    filterVariant: 'multi-select',
    size: 120,
  },
  {
    id: 'model',
    accessorFn: ({ meta }) => meta?.model,
    header: 'Model',
    filterVariant: 'multi-select',
    size: 150,
    Cell: ({ renderedCellValue }) => renderedCellValue || '-',
  },
  {
    id: 'organisation',
    accessorFn: (row) =>
      'organisation' in row ? row.organisation?.name : undefined,
    header: 'Assigned Organisation',
    size: 150,
    filterVariant: 'multi-select',
    Cell: ({ renderedCellValue, row }) => {
      const isAssigned =
        'organisation' in row.original && Boolean(row.original.organisation);
      return (
        <div className={cx(!isAssigned && 'italic')}>
          {isAssigned ? renderedCellValue : 'Not Assigned'}
        </div>
      );
    },
  },
  {
    id: 'status',
    accessorFn: (device) => getDeviceStatusDisplay(device).text,
    header: 'Status',
    filterVariant: 'multi-select',
    grow: false,
    size: 100,
    Cell: ({ row }) => {
      const { text, variant } = getDeviceStatusDisplay(row.original);
      return <StatusBadge text={text} variant={variant} />;
    },
  },
];

type DevicesContext = { devices: AnyDevice[] };

type DrawerContext =
  | ({ view: 'link' } & DevicesContext)
  | { view: 'view'; deviceId: number | null };

export const PlatformDevicesPage: FC = () => {
  const { data: devices, isFetching: isFetchingDevices, isLoading: isLoadingDevices } = useUnassociatedDevices(); // prettier-ignore
  const { data: organisations } = useOrganisations();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'view', deviceId: null });
  const {
    state: unassignDeviceConfirmationState,
    open: openUnassignDeviceConfirmation,
    toggleOpen: toggleUnassignDeviceConfirmationOpen,
  } = useOpenState<DevicesContext>({ devices: [] });
  const {
    state: deleteDeviceConfirmationState,
    open: openDeleteDeviceConfirmation,
    toggleOpen: toggleDeleteDeviceConfirmationOpen,
  } = useOpenState<{ device: AnyDevice | null }>({ device: null });

  const [addDeviceToOrganisation] = useAddDeviceToOrganisationMutation();
  const [removeDeviceFromOrganisation] = useRemoveDeviceFromOrganisationMutation(); // prettier-ignore
  const [deleteDevice] = useDeleteDeviceMutation();

  const table = useMaterialReactTable({
    ...composeTableOptions<AnyDevice>({
      enableRowActions: true,
      enableRowSelection: (row) =>
        row.original.type !== 'ALEXA' ||
        row.original.state?.connectivity?.status === 'Online',
    }),
    columns,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableStickyHeader: true,
    data: devices,
    getRowId: getEntityRowId,
    state: {
      isLoading: isLoadingDevices,
      showProgressBars: isFetchingDevices,
      showColumnFilters: true,
      rowSelection,
      ...(isLoadingDevices
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: setRowSelection,
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      <MRT_ActionMenuItem
        icon={<ViewIcon />}
        key="view"
        label="View"
        onClick={() => {
          closeMenu();
          openDrawer({ view: 'view', deviceId: row.original.id });
        }}
        table={table}
      />,
      // TODO: helper for this?
      'organisation' in row.original && !!row.original.organisation ? (
        <MRT_ActionMenuItem
          icon={<UnlinkIcon />}
          key="unlink"
          label="Unassign"
          onClick={() => {
            closeMenu();
            openUnassignDeviceConfirmation({ devices: [row.original] });
          }}
          table={table}
        />
      ) : null,
      <MRT_ActionMenuItem
        icon={<TrashIcon />}
        key="delete"
        label="Delete"
        onClick={() => {
          closeMenu();
          openDeleteDeviceConfirmation({ device: row.original });
        }}
        table={table}
      />,
    ],
  });

  const selectedDevices = getFilteredSelectedRows(table);

  const viewedDeviceId =
    drawerState.context.view === 'view' ? drawerState.context.deviceId : null;
  const viewedDevice = devices.find((device) => device.id === viewedDeviceId);

  useCloseInvalidDrawer(
    drawerState.open,
    drawerState.context.view === 'view'
      ? Boolean(viewedDevice)
      : drawerState.context.devices.length > 0,
    toggleDrawerOpen,
  );

  const getDrawerTitleProps = (): Pick<DrawerProps, 'title' | 'subtitle'> => {
    switch (drawerState.context.view) {
      case 'link':
        return {
          title: strings.devices.drawer_assign.title,
          subtitle: strings.devices.drawer_assign.subtitle,
        };
      case 'view':
        return {
          title: strings.devices.drawer_view.title,
        };
      default:
        return {
          title: '',
        };
    }
  };

  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'link': {
        const { devices } = drawerState.context;
        if (devices && devices.length > 0) {
          return (
            <AssignDevicesForm
              devices={devices}
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                const organisationId = data.organisationId;
                for (const device of devices) {
                  addDeviceToOrganisation({
                    device,
                    organisationId,
                  });
                }
                toggleDrawerOpen(false);
              }}
              organisations={organisations}
            />
          );
        }
        break;
      }
      case 'view': {
        const device = viewedDevice;
        if (device) {
          return (
            <ViewDevice
              device={device}
              key={device.id}
              onClose={() => toggleDrawerOpen(false)}
              onDelete={() => {
                openDeleteDeviceConfirmation({ device });
              }}
              onUnlink={() => {
                openUnassignDeviceConfirmation({ devices: [device] });
              }}
              unlinkType="organisation"
            />
          );
        }
        break;
      }
      default:
        break;
    }

    return null;
  };

  const hasAssignedSelectedDevice = selectedDevices.some(
    (device) => 'organisation' in device && !!device.organisation,
  );

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePostfix={strings.devices.subtitlePostfix}
          subtitlePrefix={strings.devices.subtitlePrefix}
          subtitleTooltip={strings.devices.subtitleTooltip}
          title={strings.devices.title}
          tooltip={strings.devices.platform.tooltip}
        />
      </HeadingGroupContainerLayout>
      <SelectedDevicesActionBar
        linkDeviceButtonText="Assign device"
        onLinkDevicesClick={() =>
          openDrawer({ view: 'link', devices: selectedDevices })
        }
        onUnlinkDevicesClick={() =>
          openUnassignDeviceConfirmation({ devices: selectedDevices })
        }
        selectedDevices={selectedDevices}
        unlinkDeviceButtonText="Unassign device"
        unlinkDisabled={!hasAssignedSelectedDevice}
      />
      <MaterialReactTable table={table} />
      <Drawer
        {...getDrawerTitleProps()}
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
      >
        {renderDrawerContent()}
      </Drawer>
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleUnassignDeviceConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.devices.modal_confirm_unassign.cancel}
          </Button>,
          <Button
            key="delete"
            onClick={() => {
              for (const device of unassignDeviceConfirmationState.context
                .devices) {
                if (hasOrganisation(device)) {
                  removeDeviceFromOrganisation({ device });
                }
              }
              toggleUnassignDeviceConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.devices.modal_confirm_unassign.unassign}
          </Button>,
        ]}
        description={strings.devices.modal_confirm_unassign.description}
        open={unassignDeviceConfirmationState.open}
        setOpen={toggleUnassignDeviceConfirmationOpen}
        title={strings.devices.modal_confirm_unassign.title}
      />
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleDeleteDeviceConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.devices.modal_confirm_delete.cancel}
          </Button>,
          <Button
            key="delete"
            onClick={() => {
              const device = deleteDeviceConfirmationState.context.device;
              if (device) {
                deleteDevice({ id: device.id });
              }
              toggleDeleteDeviceConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.devices.modal_confirm_delete.delete}
          </Button>,
        ]}
        description={strings.devices.modal_confirm_delete.description}
        open={deleteDeviceConfirmationState.open}
        setOpen={toggleDeleteDeviceConfirmationOpen}
        title={strings.devices.modal_confirm_delete.title}
      />
    </>
  );
};
